/**
 * VersionHistoryModal — Squarespace-style version rewind panel.
 *
 * Lists the vendor's most recent draft/publish snapshots and lets them:
 *   • Take a manual snapshot (with optional note)
 *   • Restore any snapshot into their draft (with confirmation)
 *   • Delete a snapshot they don't want in the timeline
 *
 * Snapshots are auto-created every time the vendor Publishes and before every
 * Restore, so rolling back a bad publish is always one click away.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Clock, RotateCcw, Bookmark, Trash2, Loader2, Check, Camera } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authH = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const LABEL_COLORS = {
  Published: 'text-emerald-300 border-emerald-400/40 bg-emerald-500/10',
  Manual:    'text-violet-300 border-violet-400/40 bg-violet-500/10',
  'Auto-save': 'text-white/60 border-white/15 bg-white/5',
};

function timeAgo(iso) {
  if (!iso) return '';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 7) return `${Math.floor(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function VersionHistoryModal({ open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [versions, setVersions] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [snapNote, setSnapNote] = useState('');
  const [snapping, setSnapping] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/builder/versions`, { headers: authH() });
      setVersions(r.data?.versions || []);
    } catch (err) {
      toast.error('Could not load history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  const snapshot = async () => {
    setSnapping(true);
    try {
      await axios.post(`${API}/builder/versions/snapshot`, { note: snapNote }, { headers: authH() });
      toast.success('Snapshot saved');
      setSnapNote('');
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Snapshot failed');
    } finally {
      setSnapping(false);
    }
  };

  const restore = async (version_id) => {
    setBusyId(version_id);
    try {
      await axios.post(`${API}/builder/versions/${version_id}/restore`, {}, { headers: authH() });
      toast.success('Restored to draft — hit Publish to push it live');
      window.dispatchEvent(new CustomEvent('wb:content-saved'));
      window.dispatchEvent(new CustomEvent('wb:version-restored', { detail: { version_id } }));
      setConfirmId(null);
      onClose?.();
      // Trigger a full reload so all editor state is fresh
      setTimeout(() => window.location.reload(), 250);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Restore failed');
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (version_id) => {
    setBusyId(version_id);
    try {
      await axios.delete(`${API}/builder/versions/${version_id}`, { headers: authH() });
      setVersions(v => v.filter(x => x.version_id !== version_id));
      toast.success('Version removed');
    } catch (err) {
      toast.error('Delete failed');
    } finally {
      setBusyId(null);
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-start justify-center bg-black/70 backdrop-blur-sm p-4 sm:p-10 overflow-y-auto"
      onClick={onClose}
      data-testid="version-history-modal"
    >
      <div
        className="w-full max-w-2xl bg-[#141019] border border-white/10 rounded-2xl shadow-[0_30px_80px_rgba(0,0,0,0.5)] my-8"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/8">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#8B5CF6] to-[#6D28D9] flex items-center justify-center shadow-[0_6px_20px_rgba(139,92,246,0.4)]">
              <Clock size={17} className="text-white" />
            </div>
            <div>
              <h2 className="text-white text-base font-bold tracking-tight">Version History</h2>
              <p className="text-white/50 text-xs">Rewind to any recent state — restores are always reversible.</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg text-white/50 hover:text-white hover:bg-white/5 flex items-center justify-center"
            data-testid="version-history-close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Manual snapshot */}
        <div className="px-6 py-4 border-b border-white/8 bg-white/[0.02]">
          <label className="text-white/60 text-[10px] uppercase tracking-[0.14em] font-bold flex items-center gap-1.5 mb-2">
            <Camera size={11} /> Save a named snapshot
          </label>
          <div className="flex gap-2">
            <input
              value={snapNote}
              onChange={(e) => setSnapNote(e.target.value)}
              placeholder="e.g. 'Before homepage rewrite'"
              maxLength={280}
              className="flex-1 h-9 px-3 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-white/30 focus:outline-none focus:border-violet-400/50"
              data-testid="version-snapshot-note"
            />
            <button
              onClick={snapshot}
              disabled={snapping}
              className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg text-[11px] font-bold uppercase tracking-[0.14em] bg-white/8 border border-white/12 text-white hover:bg-white/12 disabled:opacity-50"
              data-testid="version-snapshot-btn"
            >
              {snapping ? <Loader2 size={12} className="animate-spin" /> : <Bookmark size={12} />}
              Snapshot
            </button>
          </div>
        </div>

        {/* List */}
        <div className="px-2 py-2 max-h-[52vh] overflow-y-auto" data-testid="version-list">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-white/40 text-sm">
              <Loader2 size={16} className="animate-spin mr-2" /> Loading history…
            </div>
          ) : versions.length === 0 ? (
            <div className="text-center py-16 text-white/40 text-sm">
              No snapshots yet — publish or take one above to start your timeline.
            </div>
          ) : (
            versions.map((v) => {
              const chip = LABEL_COLORS[v.label] || LABEL_COLORS['Auto-save'];
              const isConfirming = confirmId === v.version_id;
              const isBusy = busyId === v.version_id;
              return (
                <div
                  key={v.version_id}
                  className="mx-2 my-1.5 p-3 rounded-xl border border-white/8 bg-white/[0.02] hover:bg-white/[0.04] transition-colors"
                  data-testid={`version-row-${v.version_id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] uppercase tracking-wider font-bold border ${chip}`}>
                          {v.label}
                        </span>
                        <span className="text-white/50 text-xs">{timeAgo(v.created_at)}</span>
                        <span className="text-white/25 text-[10px]">·</span>
                        <span className="text-white/40 text-[10px]">{v.block_count} sections</span>
                      </div>
                      {v.note && <div className="text-white/70 text-sm truncate">{v.note}</div>}
                      {!v.note && v.title && <div className="text-white/60 text-xs italic truncate">{v.title}</div>}
                    </div>

                    {isConfirming ? (
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => restore(v.version_id)}
                          disabled={isBusy}
                          className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg text-[10px] font-bold uppercase tracking-wider bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white disabled:opacity-50"
                          data-testid={`version-restore-confirm-${v.version_id}`}
                        >
                          {isBusy ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
                          Confirm restore
                        </button>
                        <button
                          onClick={() => setConfirmId(null)}
                          className="h-8 px-2.5 rounded-lg text-[10px] font-bold uppercase tracking-wider text-white/50 hover:text-white/80"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setConfirmId(v.version_id)}
                          className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg text-[10px] font-bold uppercase tracking-wider text-white/70 hover:text-white bg-white/5 hover:bg-white/12 border border-white/10"
                          data-testid={`version-restore-${v.version_id}`}
                        >
                          <RotateCcw size={11} /> Restore
                        </button>
                        <button
                          onClick={() => remove(v.version_id)}
                          disabled={isBusy}
                          className="w-8 h-8 rounded-lg text-white/40 hover:text-red-300 hover:bg-red-500/10 flex items-center justify-center disabled:opacity-40"
                          data-testid={`version-delete-${v.version_id}`}
                          title="Delete this snapshot"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-white/8 text-white/40 text-[11px]">
          Auto-snapshots on every Publish · we keep your last 50 states
        </div>
      </div>
    </div>,
    document.body
  );
}
